const SENSITIVE_KEYS = new Set([
  'username', 'password', 'pass', 'passwd', 'pwd',
  'login', 'credential', 'credentials', 'user', 'secret', 'hash',
]);

const maskVal = (val, char = '*') => {
  if (val == null) return '';
  const s = String(val);
  if (!s || s === '—') return s;
  const visible = Math.min(5, s.length);
  return s.slice(0, visible) + char.repeat(Math.max(4, s.length - visible));
};

export function exportCSV(rows, columns, filename, options = {}) {
  const isPoc = options.isPoc === true;

  const escape = (val) => {
    let s = String(val ?? '').replace(/"/g, '""');
    if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
    return /[,"\n\r]/.test(s) ? `"${s}"` : s;
  };

  const header = columns.map(c => escape(c.label)).join(',');
  const body = rows.map(row =>
    columns.map(c => {
      const raw = row[c.key];
      const val = c.format ? c.format(raw, row) : raw;
      const isSens = isPoc && SENSITIVE_KEYS.has((c.key || '').toLowerCase());
      return escape(isSens ? maskVal(val, '*') : val);
    }).join(',')
  ).join('\n');

  const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${filename}.csv`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

export function exportJSON(rows, filename) {
  const blob = new Blob([JSON.stringify(rows, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${filename}.json`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/* ═══════════════════════════════════════════════════════════════════
   StealerHunt — PDF Report Engine  v5  (HTML → html2canvas → jsPDF)
   ───────────────────────────────────────────────────────────────────
   · A4 portrait, white background, dark navy top bar
   · CONFIDENTIAL diagonal watermark (canvas overlay, post-render)
   · Left header: logo + brand   |   Right header: date + chip
   · Clean meta strip, professional table, page footer
   · POC masking: sensitive columns first-5-chars + ● repeating
   ═══════════════════════════════════════════════════════════════════ */
export async function exportPDF(rows, columns, filename, options = {}) {
  const [{ default: jsPDF }, { default: html2canvas }] = await Promise.all([
    import('jspdf'),
    import('html2canvas'),
  ]);

  const isPoc   = options.isPoc   === true;
  const orgName = options.orgName ?? '';
  const title   = (options.title ?? filename ?? 'Data Export')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

  /* ── Date / Report ID ─────────────────────────────────────────── */
  const now      = new Date();
  const dateStr  = now.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const timeStr  = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const reportId = `RPT-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

  /* ── POC masking ──────────────────────────────────────────────── */
  const processedRows = isPoc
    ? rows.map(row => {
        const out = { ...row };
        for (const col of columns) {
          if (SENSITIVE_KEYS.has((col.key || '').toLowerCase())) {
            out[col.key] = maskVal(out[col.key], '●');
          }
        }
        return out;
      })
    : rows;

  /* ── Cell text ────────────────────────────────────────────────── */
  const cellText = (raw, col, row) => {
    const val = col.format ? col.format(raw, row) : raw;
    if (val == null) return '—';
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val) || '—';
  };

  /* ── Logo: convert to base64 to avoid html2canvas CORS issues ─── */
  const toDataUrl = (url) => new Promise(resolve => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const c = document.createElement('canvas');
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext('2d').drawImage(img, 0, 0);
        resolve(c.toDataURL('image/png'));
      } catch { resolve(''); }
    };
    img.onerror = () => resolve('');
    img.src = url;
  });
  const iconB64 = await toDataUrl(`${window.location.origin}/icon-96-white.png`);

  /* ── HTML Layout Constants ────────────────────────────────────── */
  const A4_W  = 794;   // px at 96 dpi
  const MG    = 28;    // horizontal margin
  const TBL_W = A4_W - MG * 2;

  /* ── Table cells ──────────────────────────────────────────────── */
  const theadHtml = columns.map(c =>
    `<td style="background:#1E3A5F;color:#CBD5E0;font-size:9px;font-weight:700;` +
    `text-transform:uppercase;letter-spacing:0.7px;padding:9px 11px;` +
    `border-right:1px solid rgba(255,255,255,0.07);white-space:nowrap;">${c.label}</td>`
  ).join('');

  const tbodyHtml = processedRows.map((row, i) => {
    const bg    = i % 2 === 0 ? '#ffffff' : '#F8FAFC';
    const cells = columns.map(c => {
      const raw    = row[c.key];
      const val    = cellText(raw, c, row);
      const isSens = SENSITIVE_KEYS.has((c.key || '').toLowerCase());
      const extra  = isSens && isPoc
        ? 'font-family:monospace;color:#B45309;font-weight:600;'
        : '';
      return `<td style="padding:7px 11px;color:#374151;font-size:11px;border-right:1px solid #F1F5F9;word-break:break-all;${extra}">${val}</td>`;
    }).join('');
    return `<tr style="background:${bg};border-bottom:1px solid #E9EFF6;">${cells}</tr>`;
  }).join('');

  /* ── Optional POC banner ──────────────────────────────────────── */
  const pocHtml = isPoc ? `
    <div style="background:#FFFBEB;border:1px solid #F59E0B;border-left:4px solid #D97706;
         border-radius:0 6px 6px 0;padding:11px 14px;margin-bottom:18px;
         display:flex;align-items:flex-start;gap:10px;">
      <span style="font-size:18px;line-height:1.2;">⚠️</span>
      <div>
        <div style="font-size:10px;font-weight:700;color:#92400E;margin-bottom:3px;">
          POC / DEMO ACCOUNT — Sensitive Data Is Partially Masked
        </div>
        <div style="font-size:9px;color:#78350F;line-height:1.5;">
          Username and password fields show only the first 5 characters.
          Full unmasked export requires an Enterprise licence.
        </div>
      </div>
    </div>` : '';

  /* ── Logo img or fallback box ─────────────────────────────────── */
  const logoHtml = iconB64
    ? `<img src="${iconB64}" style="height:34px;width:34px;object-fit:contain;" />`
    : `<div style="height:34px;width:34px;background:#0072C0;border-radius:7px;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#fff;">SH</div>`;

  /* ── Full HTML ────────────────────────────────────────────────── */
  const html = `
<div style="width:${A4_W}px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif;
     color:#1A202C;background:#ffffff;position:relative;">

  <!-- ─── TOP BAR ─────────────────────────────────────────────── -->
  <div style="background:#0B2545;padding:18px ${MG}px;display:flex;align-items:center;
       justify-content:space-between;">
    <!-- Left: icon + brand -->
    <div style="display:flex;align-items:center;gap:12px;">
      ${logoHtml}
      <div>
        <div style="color:#ffffff;font-size:16px;font-weight:700;letter-spacing:-0.3px;">StealerHunt</div>
        <div style="color:#64748B;font-size:9px;letter-spacing:1.2px;text-transform:uppercase;margin-top:2px;">
          CTI Intelligence Platform
        </div>
      </div>
    </div>
    <!-- Right: date + chip -->
    <div style="text-align:right;">
      <div style="color:#64748B;font-size:8px;text-transform:uppercase;letter-spacing:0.8px;margin-bottom:3px;">
        Report Generated
      </div>
      <div style="color:#ffffff;font-size:15px;font-weight:600;">${dateStr}</div>
      <div style="color:#94A3B8;font-size:9px;margin-top:2px;">${timeStr} UTC</div>
      <div style="display:inline-block;background:#7F1D1D;color:#FECACA;font-size:8px;font-weight:700;
           letter-spacing:2.5px;padding:4px 10px;border-radius:3px;margin-top:7px;text-transform:uppercase;">
        CONFIDENTIAL
      </div>
    </div>
  </div>

  <!-- ─── ACCENT BAR ───────────────────────────────────────────── -->
  <div style="height:3px;background:linear-gradient(90deg,#0090F0 0%,#0072C0 100%);"></div>

  <!-- ─── BODY ─────────────────────────────────────────────────── -->
  <div style="padding:24px ${MG}px 28px;">

    ${pocHtml}

    <!-- Report title -->
    <h1 style="font-size:23px;font-weight:700;color:#0F172A;margin:0 0 3px 0;letter-spacing:-0.5px;">
      ${title}
    </h1>
    <div style="font-size:10px;color:#94A3B8;margin-bottom:18px;">
      ${filename}
    </div>

    <!-- Meta strip (4-cell card row) -->
    <div style="display:flex;border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;margin-bottom:22px;">
      <div style="flex:1;padding:13px 16px;border-right:1px solid #E2E8F0;">
        <div style="font-size:8px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;">Organization</div>
        <div style="font-size:13px;font-weight:600;color:#0F172A;">${orgName || '—'}</div>
      </div>
      <div style="flex:1;padding:13px 16px;border-right:1px solid #E2E8F0;">
        <div style="font-size:8px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;">Report ID</div>
        <div style="font-size:11px;font-weight:600;color:#0F172A;font-family:monospace;">${reportId}</div>
      </div>
      <div style="flex:1;padding:13px 16px;border-right:1px solid #E2E8F0;">
        <div style="font-size:8px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;">Total Records</div>
        <div style="font-size:24px;font-weight:700;color:#0F172A;">${rows.length.toLocaleString()}</div>
      </div>
      <div style="flex:1;padding:13px 16px;">
        <div style="font-size:8px;font-weight:600;color:#64748B;text-transform:uppercase;letter-spacing:1px;margin-bottom:5px;">Classification</div>
        <div style="font-size:13px;font-weight:700;color:#991B1B;">CONFIDENTIAL</div>
        <div style="font-size:8px;color:#94A3B8;margin-top:2px;">Restricted distribution</div>
      </div>
    </div>

    <!-- Section label -->
    <div style="display:flex;align-items:center;gap:10px;margin-bottom:10px;">
      <span style="font-size:9px;font-weight:700;color:#0072C0;text-transform:uppercase;letter-spacing:1.5px;white-space:nowrap;">
        Data Table
      </span>
      <div style="flex:1;height:1px;background:#E2E8F0;"></div>
      <span style="font-size:9px;color:#94A3B8;white-space:nowrap;">
        ${columns.length} columns · ${processedRows.length.toLocaleString()} rows
      </span>
    </div>

    <!-- Table -->
    <div style="border:1px solid #E2E8F0;border-radius:8px;overflow:hidden;">
      <table style="width:100%;border-collapse:collapse;font-size:11px;">
        <thead><tr>${theadHtml}</tr></thead>
        <tbody>${tbodyHtml}</tbody>
      </table>
    </div>

    <!-- Footer -->
    <div style="margin-top:22px;padding-top:14px;border-top:1px solid #E2E8F0;
         display:flex;justify-content:space-between;align-items:center;">
      <div style="font-size:9px;color:#94A3B8;">${orgName} · ${reportId}</div>
      <div style="font-size:9px;font-weight:600;color:#0072C0;">StealerHunt CTI Platform</div>
      <div style="font-size:9px;color:#94A3B8;">${dateStr}</div>
    </div>

  </div>
</div>`;

  /* ── Render into hidden off-screen container ──────────────────── */
  const wrapper = document.createElement('div');
  wrapper.style.cssText = `position:fixed;top:-99999px;left:-99999px;z-index:-9999;width:${A4_W}px;`;
  wrapper.innerHTML = html;
  document.body.appendChild(wrapper);

  await new Promise(r => setTimeout(r, 180)); // let images paint

  const canvas = await html2canvas(wrapper.firstElementChild, {
    scale       : 2,
    useCORS     : true,
    allowTaint  : false,
    backgroundColor: '#ffffff',
    logging     : false,
    width       : A4_W,
    windowWidth : A4_W,
  });

  document.body.removeChild(wrapper);

  /* ── CONFIDENTIAL watermark drawn directly on canvas ─────────── */
  const wctx = canvas.getContext('2d');
  wctx.save();
  wctx.font        = `bold ${62 * 2}px Arial`;   // scale=2
  wctx.fillStyle   = 'rgba(127, 29, 29, 0.055)';
  wctx.textAlign   = 'center';
  wctx.textBaseline = 'middle';
  const step_x = 520 * 2;
  const step_y = 290 * 2;
  for (let cy = step_y / 2; cy < canvas.height + step_y; cy += step_y) {
    for (let cx = -step_x / 2; cx < canvas.width + step_x; cx += step_x) {
      wctx.save();
      wctx.translate(cx, cy);
      wctx.rotate(-35 * Math.PI / 180);
      wctx.fillText('CONFIDENTIAL', 0, 0);
      wctx.restore();
    }
  }
  wctx.restore();

  /* ── Split canvas into A4 page slices ────────────────────────── */
  // canvas.width == A4_W * 2 (scale=2)
  // A4 height in canvas pixels (scale=2): proportional to A4 ratio
  const A4_H_CANVAS = Math.round(canvas.width * 297 / 210);
  const totalPages  = Math.ceil(canvas.height / A4_H_CANVAS);

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  for (let p = 0; p < totalPages; p++) {
    if (p > 0) doc.addPage();

    const srcY    = p * A4_H_CANVAS;
    const sliceH  = Math.min(A4_H_CANVAS, canvas.height - srcY);
    const heightMM = sliceH / canvas.width * 210; // preserve aspect ratio

    const slice     = document.createElement('canvas');
    slice.width     = canvas.width;
    slice.height    = sliceH;
    const sctx      = slice.getContext('2d');
    sctx.fillStyle  = '#ffffff';
    sctx.fillRect(0, 0, canvas.width, sliceH);
    sctx.drawImage(canvas, 0, srcY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

    const imgData = slice.toDataURL('image/jpeg', 0.93);
    doc.addImage(imgData, 'JPEG', 0, 0, 210, heightMM);
  }

  doc.save(`${filename || 'stealerhunt-report'}.pdf`);
}
